<nav class="navbar" id="navbar">
   <!-- Cookie Consent Banner -->
   @if (!cookiesAccepted) {
   <div id="cookie-consent-banner" class="bg-slate-100 text-slate-800 border-b border-slate-200 dark:bg-slate-950 dark:text-slate-200 dark:border-slate-800">
      <div class="container relative flex flex-wrap items-center justify-center gap-x-2 gap-y-1 py-2 text-sm text-center">
         <span>{{ 'components.navbar.cookies.policy' | translate }}</span>
         <a routerLink="/about" target="_blank" class="underline hover:text-cyan-600 dark:hover:text-cyan-300">{{ 'components.footer.policy' | translate }}</a>
         <button id="accept-cookies" class="button ms-2" (click)="acceptCookies()">
            {{ 'components.navbar.cookies.accept' | translate }}
         </button>
      </div>
   </div>
   }
   <div class="container relative flex flex-nowrap items-center justify-between gap-3">
      <!-- The logo is the way home, so the bar needs no separate Home button -->
      <a routerLink="/" class="inline-flex items-center gap-2 min-h-11 shrink-0 text-slate-900 dark:text-white"
         [attr.aria-label]="'components.login-link.home' | translate">
         <!-- The same mark as assets/images/logo.svg, in its coral -->
         <svg viewBox="0 0 32 32" width="32" height="32" aria-hidden="true" class="size-8 text-[#e66f58]"
            fill="none" stroke="currentColor" stroke-linecap="round">
            <circle cx="13.5" cy="13.5" r="9" stroke-width="3.4" />
            <path d="M8.6 14.6c1.6-2.6 3.3-2.6 4.9 0s3.3 2.6 4.9 0" stroke-width="2.6" />
            <path d="M20.4 20.4L28 28" stroke-width="4.2" />
         </svg>
         <span class="text-xl font-bold tracking-tight">Oopsy</span>
      </a>

      <div class="nav-icons flex items-center gap-2 shrink-0">
         @if (userDisplayName) {
            <a routerLink="/business" class="glass-pill inline-flex items-center justify-center size-11 text-cyan-600 dark:text-cyan-300"
               [attr.aria-label]="'components.navbar.myShops' | translate" [attr.title]="'components.navbar.myShops' | translate">
               <i class="mdi mdi-storefront-outline text-xl leading-none" aria-hidden="true"></i>
            </a>
         }

         @if (!isMobile) {
            <button type="button" class="glass-pill inline-flex items-center justify-center size-11 text-cyan-600 dark:text-cyan-300"
               [attr.aria-label]="'components.navbar.darkTheme' | translate" [attr.title]="'components.navbar.darkTheme' | translate"
               [attr.aria-pressed]="theme.isDark" (click)="theme.toggle()">
               <i class="mdi text-xl leading-none" aria-hidden="true" [class.mdi-weather-night]="!theme.isDark" [class.mdi-white-balance-sunny]="theme.isDark"></i>
            </button>
            <app-language />
         }

         @if (!userDisplayName) {
            <login-link [login]="true" />
         }

         <!-- Signed in: the account menu, on every width. Signed out: only a phone needs a menu (language, theme) -->
         @if (userDisplayName || isMobile) {
            <button type="button" class="glass-pill inline-flex items-center gap-1.5 h-11 ps-1.5 pe-3 text-cyan-600 dark:text-cyan-300"
               [class.ps-3]="!userDisplayName"
               [attr.aria-label]="userDisplayName ? userDisplayName + ' — ' + ('components.navbar.menu' | translate) : ('components.navbar.menu' | translate)"
               [attr.aria-expanded]="menuOpen" aria-controls="navbar-menu" aria-haspopup="true" (click)="toggleMenu($event)">
               @if (userDisplayName) {
                  <span aria-hidden="true"
                     class="size-8 inline-flex items-center justify-center rounded-full bg-cyan-600 !text-white text-sm font-semibold shrink-0">{{ initials }}</span>
               }
               @if (isMobile) {
                  <i class="mdi text-2xl leading-none" aria-hidden="true" [class.mdi-menu]="!menuOpen" [class.mdi-close]="menuOpen"></i>
               } @else {
                  <span class="truncate max-w-[10rem] text-slate-800 dark:text-slate-100">{{ userDisplayName }}</span>
                  <i class="mdi text-xl leading-none transition-transform" aria-hidden="true" [class.mdi-chevron-down]="true" [class.rotate-180]="menuOpen"></i>
               }
            </button>
         }
      </div>

      @if (menuOpen) {
         @if (isMobile) {
            <!-- Dims the page so the menu stands apart from what is behind it; a tap on it closes the menu -->
            <div class="fixed inset-0 -z-10 bg-black/30" aria-hidden="true"></div>
         }
         <div id="navbar-menu" class="absolute end-3 top-full mt-2 w-72 rounded-2xl p-2 flex flex-col z-999 bg-white text-slate-800 border border-slate-200 shadow-2xl dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700"
            (click)="$event.stopPropagation()">
            @if (userDisplayName) {
               <div class="flex items-center gap-3 min-w-0 px-3 py-2">
                  <span aria-hidden="true"
                     class="size-8 inline-flex items-center justify-center rounded-full bg-cyan-600 text-white text-sm font-semibold shrink-0">{{ initials }}</span>
                  <span class="truncate font-medium">{{ userDisplayName }}</span>
               </div>
               <hr class="my-1 border-slate-200 dark:border-slate-700" />
               <a routerLink="/business" class="flex items-center gap-3 w-full min-h-11 px-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
                  <i class="mdi mdi-storefront-outline text-xl leading-none text-slate-500 dark:text-slate-400" aria-hidden="true"></i>
                  <span class="flex-1">{{ 'components.navbar.myShops' | translate }}</span>
               </a>
            }
            @if (isMobile) {
               <app-language [inMenu]="true" />
               <button type="button" role="switch" [attr.aria-checked]="theme.isDark" (click)="theme.toggle()"
                  class="flex items-center gap-3 w-full min-h-11 px-3 rounded-xl text-start hover:bg-slate-100 dark:hover:bg-slate-800">
                  <i class="mdi mdi-weather-night text-xl leading-none text-slate-500 dark:text-slate-400" aria-hidden="true"></i>
                  <span class="flex-1">{{ 'components.navbar.darkTheme' | translate }}</span>
                  <span aria-hidden="true" class="relative w-10 h-6 rounded-full transition-colors"
                     [class.bg-cyan-600]="theme.isDark" [class.bg-slate-300]="!theme.isDark">
                     <span class="absolute top-0.5 start-0.5 size-5 rounded-full bg-white shadow transition-transform"
                        [class.translate-x-4]="theme.isDark"></span>
                  </span>
               </button>
            }
            @if (userDisplayName) {
               <!-- Log out last and set apart: rare, and it costs a sign-in to undo -->
               <hr class="my-1 border-slate-200 dark:border-slate-700" />
               <button type="button" (click)="logout()"
                  class="flex items-center gap-3 w-full min-h-11 px-3 rounded-xl text-start text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
                  <i class="mdi mdi-logout text-xl leading-none" aria-hidden="true"></i>
                  <span>{{ 'components.login-link.logout' | translate }}</span>
               </button>
            }
         </div>
      }
   </div>
</nav>
